import { z } from 'zod';
import { User as WorkOSUser } from '@workos-inc/node';

export const RoleSchema = z.enum(['admin', 'user']);

export type Role = z.infer<typeof RoleSchema>;

export const PermissionSchema = z.enum(['widgets:users-table:manage']);

export type Permission = z.infer<typeof PermissionSchema>;

export const UserSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  organizationId: z.string(),
  role: RoleSchema,
  permissions: z.array(PermissionSchema),
});

// User/Account types
export type User = z.infer<typeof UserSchema>;

// JWT Payload schema
export const JwtPayloadSchema = z.object({
  sub: z.string(),
  org_id: z.string(),
  role: z.string(),
  permissions: z.array(z.string()),
  entitlements: z.array(z.string()).optional(),
  exp: z.number(),
  iat: z.number(),
  sid: z.string(),
});

export type JwtPayload = z.infer<typeof JwtPayloadSchema>;

const mapWorkOsRoleToRole = (role: string): Role => {
  switch (role) {
    case 'admin':
      return 'admin';
  }
  return 'user';
};

const mapWorkOsPermissionToPermission = (permission: string): Permission | null => {
  switch (permission) {
    case 'widgets:users-table:manage':
      return 'widgets:users-table:manage';
  }
  return null;
};

export const mapWorkOsUserToUser = (args: {
  workOsUser: WorkOSUser;
  organizationId: string;
  role: string;
  permissions: string[];
}): User => {
  const { workOsUser, organizationId, role, permissions } = args;
  const nextPermissions: Permission[] = [];
  permissions.forEach(permission => {
    const mappedPermission = mapWorkOsPermissionToPermission(permission);
    if (mappedPermission) {
      nextPermissions.push(mappedPermission);
    }
  });
  return {
    id: workOsUser.id,
    name: `${workOsUser.firstName} ${workOsUser.lastName}`.trim() || workOsUser.email,
    email: workOsUser.email,
    organizationId,
    role: mapWorkOsRoleToRole(role),
    permissions: nextPermissions,
  };
};
